import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { EventClickArg, EventDropArg, DateSelectArg } from '@fullcalendar/core';
import deLocale from '@fullcalendar/core/locales/de';
import itLocale from '@fullcalendar/core/locales/it';
import { Post, PostStatus } from '../store/posts';
import { Flight } from '../store/flights';
import { CalendarContextMenu } from './CalendarContextMenu';

// Helper to lighten a hex color for background
function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.floor((num >> 16) + (255 - (num >> 16)) * percent));
  const g = Math.min(255, Math.floor(((num >> 8) & 0x00FF) + (255 - ((num >> 8) & 0x00FF)) * percent));
  const b = Math.min(255, Math.floor((num & 0x0000FF) + (255 - (num & 0x0000FF)) * percent));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// Helper to get contrasting text color
function getContrastColor(hex: string): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1f2937' : '#ffffff';
}

// Status colors for calendar events
const STATUS_COLORS: Record<PostStatus, { bg: string; border: string; text: string }> = {
  draft: { bg: '#f3f4f6', border: '#9ca3af', text: '#374151' },
  scheduled: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  publishing: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  published: { bg: '#d1fae5', border: '#10b981', text: '#065f46' },
  partial: { bg: '#ffedd5', border: '#f97316', text: '#c2410c' },
  failed: { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' },
  cancelled: { bg: '#f3f4f6', border: '#6b7280', text: '#4b5563' },
};

// STATUS_LABELS removed - using i18n t('posts:status.*') instead

// Status icons for quick visual recognition
const StatusIcon = ({ status }: { status: PostStatus }) => {
  switch (status) {
    case 'draft':
      return (
        <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      );
    case 'scheduled':
      return (
        <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'publishing':
      return (
        <svg className="w-3.5 h-3.5 text-amber-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      );
    case 'published':
      return (
        <svg className="w-3.5 h-3.5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      );
    case 'partial':
      return (
        <svg className="w-3.5 h-3.5 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V5z" clipRule="evenodd" />
        </svg>
      );
    case 'failed':
      return (
        <svg className="w-3.5 h-3.5 text-red-600" fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      );
    case 'cancelled':
      return (
        <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      );
  }
};

export type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek';

export interface CalendarAction {
  type: 'new_post' | 'new_flight' | 'move_post';
  date: Date;
  post?: Post; // For move_post action
}

interface PostCalendarProps {
  posts: Post[];
  flights?: Flight[];
  onEventClick: (post: Post) => void;
  onFlightClick?: (flight: Flight) => void;
  onEventDrop: (postId: string, newDate: Date) => void;
  onDateSelect: (start: Date, end: Date) => void;
  onCalendarAction?: (action: CalendarAction) => void;
  currentView?: CalendarView;
  onViewChange?: (view: CalendarView) => void;
  showContextMenu?: boolean;
}

export function PostCalendar({ posts, flights = [], onEventClick, onFlightClick, onEventDrop, onDateSelect, onCalendarAction, currentView: externalView, onViewChange, showContextMenu = false }: PostCalendarProps) {
  const { t, i18n } = useTranslation(['posts', 'common']);
  const calendarRef = useRef<FullCalendar>(null);
  const [internalView, setInternalView] = useState<CalendarView>('dayGridMonth');

  // Get FullCalendar locale based on current language
  const calendarLocale = i18n.language === 'it' ? itLocale : deLocale;

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
    selectedDate: Date;
  }>({
    isOpen: false,
    position: { x: 0, y: 0 },
    selectedDate: new Date(),
  });

  // Create a map of flight_id to flight for quick lookup
  const flightMap = new Map(flights.map(f => [f.id, f]));

  // Use external view if provided, otherwise use internal state
  const currentView = externalView ?? internalView;
  const setCurrentView = (view: CalendarView) => {
    if (onViewChange) {
      onViewChange(view);
    } else {
      setInternalView(view);
    }
  };

  // Sync calendar view when currentView changes (e.g., after posts reload)
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (api && api.view.type !== currentView) {
      api.changeView(currentView);
    }
  }, [currentView, posts]); // Also run when posts change to restore view after reload

  // Convert flights to visible all-day events (campaign bars at top of days)
  const flightEvents = flights.map((flight) => {
    const flightColor = flight.color || '#6366f1';
    // Add one day to end date so it includes the end day (FullCalendar end is exclusive)
    // Parse as local date to avoid timezone issues (YYYY-MM-DD format)
    const [endYear, endMonth, endDay] = flight.end_date.split('T')[0].split('-').map(Number);
    const endDate = new Date(endYear, endMonth - 1, endDay + 1);
    const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    return {
      id: `flight-${flight.id}`,
      title: flight.name,
      start: flight.start_date.split('T')[0], // Ensure only date part is used
      end: endDateStr,
      allDay: true,
      backgroundColor: flightColor,
      borderColor: flightColor,
      textColor: getContrastColor(flightColor),
      classNames: ['flight-event'],
      extendedProps: {
        type: 'flight',
        flight,
      },
    };
  });

  // Convert posts to calendar events
  const postEvents = posts.map((post) => {
    const statusColors = STATUS_COLORS[post.status];
    const flight = post.flight_id ? flightMap.get(post.flight_id) : null;
    const flightColor = (flight as Flight & { color?: string })?.color;

    // Use flight color if available, otherwise fall back to status colors
    let backgroundColor: string;
    let borderColor: string;
    let textColor: string;

    if (flightColor) {
      backgroundColor = lightenColor(flightColor, 0.7);
      borderColor = flightColor;
      textColor = getContrastColor(backgroundColor);
    } else {
      backgroundColor = statusColors.bg;
      borderColor = statusColors.border;
      textColor = statusColors.text;
    }

    return {
      id: post.id,
      title: post.text.substring(0, 50),
      start: post.scheduled_for,
      allDay: false,
      backgroundColor,
      borderColor,
      textColor,
      extendedProps: {
        type: 'post',
        post,
        status: post.status,
        platforms: post.platforms,
        flight,
        flightColor,
      },
    };
  });

  // Combine all events
  const events = [...flightEvents, ...postEvents];

  const handleEventClick = (info: EventClickArg) => {
    // Handle flight clicks
    if (info.event.extendedProps.type === 'flight') {
      const flight = info.event.extendedProps.flight as Flight;
      if (onFlightClick) {
        onFlightClick(flight);
      }
      return;
    }
    // Handle post clicks
    const post = info.event.extendedProps.post as Post;
    onEventClick(post);
  };

  const handleEventDrop = (info: EventDropArg) => {
    // Don't allow dragging flight background events
    if (info.event.extendedProps.type === 'flight') {
      info.revert();
      return;
    }

    const postId = info.event.id;
    const newDate = info.event.start;

    // Only allow dropping for draft and scheduled posts
    const post = info.event.extendedProps.post as Post;
    if (!['draft', 'scheduled'].includes(post.status)) {
      info.revert();
      return;
    }

    if (newDate) {
      onEventDrop(postId, newDate);
    }
  };

  const handleDateSelect = (info: DateSelectArg) => {
    // If context menu is enabled and we have an action handler, show the menu
    if (showContextMenu && onCalendarAction) {
      // Get click position from jsEvent
      const jsEvent = info.jsEvent;
      if (jsEvent) {
        setContextMenu({
          isOpen: true,
          position: { x: jsEvent.clientX, y: jsEvent.clientY },
          selectedDate: info.start,
        });
        // Unselect the calendar selection
        calendarRef.current?.getApi().unselect();
        return;
      }
    }
    // Default behavior - call onDateSelect
    onDateSelect(info.start, info.end);
  };

  // Handle context menu actions
  const handleContextMenuAction = (actionType: 'new_post' | 'new_flight' | 'move_post', post?: Post) => {
    if (onCalendarAction) {
      onCalendarAction({
        type: actionType,
        date: contextMenu.selectedDate,
        post,
      });
    }
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  };

  // Custom event rendering with thumbnail and flight color
  const renderEventContent = (eventInfo: { event: { extendedProps: { type?: string; post?: Post; platforms?: string[]; flight?: Flight; flightColor?: string }; title: string; allDay: boolean }; timeText: string }) => {
    const eventType = eventInfo.event.extendedProps.type;

    // Render Flight events (all-day campaign bars)
    if (eventType === 'flight') {
      const flight = eventInfo.event.extendedProps.flight as Flight;
      return (
        <div className="px-2 py-1 flex items-center gap-2 cursor-pointer hover:opacity-90 transition-opacity">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="font-medium truncate">{flight.name}</span>
          <span className="text-[10px] opacity-75 ml-auto">{t('posts:calendar.campaign')}</span>
        </div>
      );
    }

    // Render Post events
    const post = eventInfo.event.extendedProps.post as Post;
    const platforms = eventInfo.event.extendedProps.platforms || [];
    const flight = eventInfo.event.extendedProps.flight;
    const flightColor = eventInfo.event.extendedProps.flightColor;

    // Get first media URL for thumbnail
    const thumbnailUrl = post.media_urls && post.media_urls.length > 0 ? post.media_urls[0] : null;
    const hasMoreMedia = post.media_urls && post.media_urls.length > 1;

    return (
      <div className="p-1 overflow-hidden text-xs flex gap-1.5">
        {/* Thumbnail */}
        {thumbnailUrl && (
          <div className="flex-shrink-0 relative">
            <img
              src={thumbnailUrl}
              alt=""
              className="w-8 h-8 object-cover rounded"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            {hasMoreMedia && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-gray-800 text-white text-[8px] rounded-full flex items-center justify-center">
                +{post.media_urls.length - 1}
              </span>
            )}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-0.5">
            {/* Status Icon */}
            <StatusIcon status={post.status} />
            {/* Platform badges */}
            {platforms.includes('facebook') && (
              <span className="w-3 h-3 rounded-full bg-blue-600 flex items-center justify-center text-white text-[8px]">f</span>
            )}
            {platforms.includes('instagram') && (
              <span className="w-3 h-3 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center text-white text-[8px]">i</span>
            )}
            <span className="font-medium truncate">{eventInfo.timeText}</span>
          </div>
          <div className="truncate">{post.text.substring(0, 25)}{post.text.length > 25 ? '...' : ''}</div>
          {/* Flight badge */}
          {flight && (
            <div
              className="mt-0.5 px-1 py-0.5 rounded text-[9px] truncate inline-block"
              style={{
                backgroundColor: flightColor || '#6366f1',
                color: flightColor ? getContrastColor(flightColor) : '#ffffff',
              }}
            >
              {flight.name}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      {/* View Toggle */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2">
          <button
            onClick={() => {
              setCurrentView('dayGridMonth');
              calendarRef.current?.getApi().changeView('dayGridMonth');
            }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              currentView === 'dayGridMonth'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('posts:calendar.month')}
          </button>
          <button
            onClick={() => {
              setCurrentView('timeGridWeek');
              calendarRef.current?.getApi().changeView('timeGridWeek');
            }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              currentView === 'timeGridWeek'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('posts:calendar.week')}
          </button>
          <button
            onClick={() => {
              setCurrentView('timeGridDay');
              calendarRef.current?.getApi().changeView('timeGridDay');
            }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              currentView === 'timeGridDay'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('posts:calendar.day')}
          </button>
          <button
            onClick={() => {
              setCurrentView('listWeek');
              calendarRef.current?.getApi().changeView('listWeek');
            }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              currentView === 'listWeek'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('posts:calendar.list')}
          </button>
        </div>

        {/* Legend - Status */}
        <div className="flex gap-4 text-xs">
          {(['draft', 'scheduled', 'published', 'failed'] as PostStatus[]).map((status) => (
            <div key={status} className="flex items-center gap-1.5">
              <StatusIcon status={status} />
              <span className="text-gray-600">{t(`posts:status.${status}`)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Flights Legend (if any) */}
      {flights.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4 px-1">
          <span className="text-xs text-gray-500 self-center">{t('posts:calendar.campaigns')}:</span>
          {flights.map((flight) => (
            <a
              key={flight.id}
              href={`/flights/${flight.id}`}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium hover:opacity-80 transition-opacity"
              style={{
                backgroundColor: lightenColor(flight.color || '#6366f1', 0.85),
                color: getContrastColor(lightenColor(flight.color || '#6366f1', 0.4)),
                borderLeft: `3px solid ${flight.color || '#6366f1'}`,
              }}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: flight.color || '#6366f1' }}
              />
              {flight.name}
            </a>
          ))}
        </div>
      )}

      {/* Calendar */}
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
        initialView="dayGridMonth"
        locale={calendarLocale}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: '',
        }}
        buttonText={{
          today: t('posts:calendar.today'),
          month: t('posts:calendar.month'),
          week: t('posts:calendar.week'),
          day: t('posts:calendar.day'),
          list: t('posts:calendar.list'),
        }}
        events={events}
        eventClick={handleEventClick}
        eventDrop={handleEventDrop}
        select={handleDateSelect}
        eventContent={renderEventContent}
        editable={true}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={3}
        weekends={true}
        nowIndicator={true}
        slotMinTime="06:00:00"
        slotMaxTime="22:00:00"
        slotDuration="00:30:00"
        allDaySlot={true}
        allDayText={t('posts:calendar.campaigns')}
        height="auto"
        eventTimeFormat={{
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }}
        slotLabelFormat={{
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }}
        firstDay={1} // Monday
      />

      {/* Context Menu */}
      {showContextMenu && (
        <CalendarContextMenu
          isOpen={contextMenu.isOpen}
          position={contextMenu.position}
          selectedDate={contextMenu.selectedDate}
          posts={posts}
          onClose={() => setContextMenu(prev => ({ ...prev, isOpen: false }))}
          onNewPost={() => handleContextMenuAction('new_post')}
          onNewFlight={() => handleContextMenuAction('new_flight')}
          onMovePost={(post) => handleContextMenuAction('move_post', post)}
        />
      )}
    </div>
  );
}
